import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle
} from "react";
import styles from "./styles.module.scss";
import { LazyImageAspectFit } from "../../LazyImageAspectFit";
import { ImageUtilities } from "../../../models/ImageUtilities";

export const Album3DRepresentation = forwardRef<
  Album3DRepresentationHandle,
  Album3DRepresentationProps
>(
  (
    {
      frontCover,
      backCover,
      spine,
      frontThumbnail,
      backThumbnail,
      spineThumbnail,
      defaultCover,
      defaultBack,
      defaultSpine,
      initialRotation,
      isLoadingFrontCover,
      size,
      setIsDraggingExternal
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = useState(false);
    const drag = useRef<DragState>({ active: false, lastX: 0, lastY: 0 });
    useImperativeHandle(ref, () => ({
      reset: () => {
        setRotX(0);
        setRotY(0);
      }
    }));
    useEffect(() => {
      if (setIsDraggingExternal) {
        setIsDraggingExternal(isDragging);
      }
    }, [isDragging]);

    const [rotX, setRotX] = useState(0);
    const [rotY, setRotY] = useState(initialRotation ?? 0);

    const [fillColor, setFillColor] = useState("#000");

    const [width, setWidth] = useState(size);
    const [height, setHeight] = useState(size);
    useEffect(() => {
      const imageData = frontCover ? `data:image/png;base64,${frontCover}` : undefined;
      const thumbnailData = frontThumbnail ? `data:image/png;base64,${frontThumbnail}` : undefined;
      const image = new Image();
      image.src = imageData ?? thumbnailData ?? defaultCover;
      image.onload = function () {
        const reference = Math.max(image.width, image.height);
        const scale = size / reference;
        const finalHeight = image.height * scale;
        setWidth(image.width * scale);
        setHeight(finalHeight);
        setFillColor(ImageUtilities.getDominantColor(image));
        calculateDepth(finalHeight);
      };
    }, [frontCover, spine]);

    const DEPTH_RATIO = 0.03;
    const [depth, setDepth] = useState(Math.round(size * DEPTH_RATIO));
    const calculateDepth = (reference: number) => {
      const imageData = spine ? `data:image/png;base64,${spine}` : undefined;
      const thumbnailData = spineThumbnail ? `data:image/png;base64,${spineThumbnail}` : undefined;
      const image = new Image();
      image.src = imageData ?? thumbnailData ?? defaultSpine;
      image.onload = function () {
        const scale = reference / Math.max(image.width, image.height);
        setDepth(Math.min(image.width, image.height) * scale);
      };
    };

    // ── pointer handlers ──────────────────────────────────────────────────────
    const onPointerDown = useCallback((e: React.PointerEvent) => {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      drag.current = { active: true, lastX: e.clientX, lastY: e.clientY };
      setIsDragging(true);
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
      if (!drag.current.active) return;
      const dx = e.clientX - drag.current.lastX;
      const dy = e.clientY - drag.current.lastY;
      drag.current.lastX = e.clientX;
      drag.current.lastY = e.clientY;
      setRotY(prev => prev + dx * 0.45);
      setRotX(prev => prev - dy * 0.45);
    }, []);

    const onPointerUp = useCallback(() => {
      setIsDragging(false);
      drag.current.active = false;
    }, []);

    // ── touch handlers (mobile) ───────────────────────────────────────────────
    const lastTouch = useRef<{ x: number; y: number } | null>(null);

    const onTouchStart = useCallback((e: React.TouchEvent) => {
      const t = e.touches[0];
      lastTouch.current = { x: t.clientX, y: t.clientY };
    }, []);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
      if (!lastTouch.current) return;
      const t = e.touches[0];
      const dx = t.clientX - lastTouch.current.x;
      const dy = t.clientY - lastTouch.current.y;
      lastTouch.current = { x: t.clientX, y: t.clientY };
      setRotY(prev => prev + dx * 0.45);
      setRotX(prev => prev - dy * 0.45);
    }, []);

    const halfW = width / 2;
    const halfH = height / 2;
    const halfD = depth / 2;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none"
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={() => (lastTouch.current = null)}
      >
        {/* perspective container */}
        <div
          style={{
            perspective: size * 3.5,
            perspectiveOrigin: "50% 50%"
          }}
        >
          {/* rotation wrapper */}
          <div
            style={{
              width: width,
              height: height,
              position: "relative",
              transformStyle: "preserve-3d",
              transform: `rotateX(${rotX}deg) rotateY(${rotY}deg)`,
              transition: isDragging ? "none" : "transform 0.05s linear"
            }}
          >
            {/* FRONT */}
            <div
              className={styles.face}
              style={{
                width: width,
                height: height,
                top: 0,
                left: 0
              }}
            >
              <LazyImageAspectFit
                className={styles.image}
                image={frontCover}
                thumbnail={frontThumbnail}
                defaultImage={defaultCover}
                alt="Front cover"
              />
              {isLoadingFrontCover && <div className={styles.blurDiv} />}
            </div>

            {/* BACK */}
            <div
              className={styles.face}
              style={{
                width: width,
                height: height,
                top: 0,
                left: 0,
                transform: `translateZ(${-depth}px) rotateY(180deg)`
              }}
            >
              <LazyImageAspectFit
                className={styles.image}
                imageClassName={styles.backImage}
                image={backCover}
                thumbnail={backThumbnail}
                defaultImage={defaultBack}
                alt="Back cover"
              />
            </div>

            {/* LEFT (spine) */}
            <div
              className={styles.face}
              style={{
                width: depth,
                height: height,
                top: 0,
                left: halfW - halfD,
                transform: `translateX(${-halfW - halfD}px) rotateY(-90deg)`,
                transformOrigin: "right center"
              }}
            >
              <LazyImageAspectFit
                className={styles.image}
                image={spine}
                thumbnail={spineThumbnail}
                defaultImage={defaultSpine}
                forceVertical
                alt="Spine"
              />
              {frontCover && !spine && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backgroundColor: fillColor,
                    opacity: 0.5,
                    top: 0
                  }}
                />
              )}
            </div>

            {/* RIGHT (filler) */}
            <div
              className={styles.face}
              style={{
                width: depth,
                height: height,
                top: 0,
                left: halfW - halfD,
                background: fillColor,
                transform: `translateX(${halfW + halfD}px) rotateY(90deg)`,
                transformOrigin: "left center"
              }}
            />

            {/* TOP (filler) */}
            <div
              className={styles.face}
              style={{
                width: width,
                height: depth,
                top: halfH - halfD,
                left: 0,
                background: fillColor,
                transform: `translateY(${-halfH - halfD}px) rotateX(90deg)`,
                transformOrigin: "center bottom"
              }}
            />

            {/* BOTTOM (filler) */}
            <div
              className={styles.face}
              style={{
                width: width,
                height: depth,
                top: halfH - halfD,
                left: 0,
                background: fillColor,
                transform: `translateY(${halfH + halfD}px) rotateX(-90deg)`,
                transformOrigin: "center top"
              }}
            />
          </div>
        </div>
      </div>
    );
  }
);

interface DragState {
  active: boolean;
  lastX: number;
  lastY: number;
}

export interface Album3DRepresentationHandle {
  reset: () => void;
}

interface Album3DRepresentationProps {
  frontCover: string | undefined;
  backCover: string | undefined;
  spine: string | undefined;
  frontThumbnail: string | undefined;
  backThumbnail: string | undefined;
  spineThumbnail: string | undefined;
  defaultCover: string;
  defaultBack: string;
  defaultSpine: string;
  isLoadingFrontCover: boolean;
  initialRotation?: number;
  size?: number;
  fillColor?: string;
  setIsDraggingExternal?: (isDragging: boolean) => void;
}
